import { TaskAdmin } from '@gleamer/types';
import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAdminTaskDetails } from '../../../../hooks/useAdminTasks';
import { CopyButton } from '../CopyButton';
import { formatDateTime } from '../imports/format';
import { TraceLink } from '../TraceLink';
import { ItemsSection } from './ItemsSection';
import { PredictionsSection } from './PredictionsSection';
import { IssuesIndicator, StatusBadge } from './StatusBadge';
import {
  computationIssues,
  labellersOf,
  reviewersOf,
  taskStatus,
} from './tasks';

type AdminLabelTask = TaskAdmin.AdminLabelTask;

function Row({ label, children }: React.PropsWithChildren<{ label: string }>) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-3 border-b border-dl-border py-2">
      <dt className="text-sm font-medium text-dl-muted">{label}</dt>
      <dd className="min-w-0 text-dl-text">{children}</dd>
    </div>
  );
}

function Table({ value }: { value?: string | null }) {
  return value ? (
    <span className="flex items-start gap-2">
      <span className="min-w-0 break-all font-mono text-sm">{value}</span>
      <CopyButton text={value} />
    </span>
  ) : (
    <span className="text-dl-faint">—</span>
  );
}

function People({ emails }: { emails?: string[] | null }) {
  if (!emails || emails.length === 0) {
    return <span className="text-dl-faint">None</span>;
  }
  return (
    <ul className="flex flex-wrap gap-1">
      {emails.map(email => (
        <li
          key={email}
          className="rounded-full bg-dl-sunken px-2 py-0.5 text-sm ring-1 ring-dl-border"
        >
          {email}
        </li>
      ))}
    </ul>
  );
}

/** Does the task name a prediction table (its details say), or has it had an import (the list says)? */
function hasPredictions(task: AdminLabelTask): boolean {
  return (
    !!task.predictionsConfiguration?.bigQueryPredictionTableId ||
    task.predictionImportId != null
  );
}

/**
 * A task opened from the list: what the list already says at once, then its taskers, instructions and
 * predictions configuration from its details (GET /api/label-tasks/{taskId}/details), and what is happening to
 * it: the computations of its items and the imports of its predictions. Escape closes it.
 */
export function TaskDrawer({
  taskId,
  listed,
  onClose,
  canDuplicate = false,
  canSettlePredictions = false,
  canStopComputations = false,
}: {
  taskId: number;
  listed?: AdminLabelTask;
  onClose: () => void;
  /** create:labelTask: the drawer offers to start a new task from this one. */
  canDuplicate?: boolean;
  /** create:labelTask: the drawer offers to import the predictions again, replay, accept or abandon an import. */
  canSettlePredictions?: boolean;
  /** update:labelTask: the drawer offers to stop a computation of the task's items that runs. */
  canStopComputations?: boolean;
}) {
  const details = useAdminTaskDetails(taskId);
  const task: AdminLabelTask | undefined = details.data ?? listed;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <aside
      role="dialog"
      aria-label={task ? `Task ${task.name}` : `Task ${taskId}`}
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-xl flex-col overflow-y-auto bg-dl-surface shadow-2xl ring-1 ring-dl-border"
    >
      <div className="sticky top-0 flex items-start gap-3 border-b border-dl-border bg-dl-surface px-6 py-5">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-sm text-dl-faint">Task #{taskId}</p>
          <h3 className="break-words text-xl font-semibold text-dl-text">
            {task?.name ?? '…'}
          </h3>
        </div>
        {canDuplicate && (
          <Link
            to={`/backoffice/tasks/new?from=${taskId}`}
            className="rounded-lg border border-dl-border px-3 py-1 text-sm font-medium text-dl-text transition hover:border-dl-strong hover:bg-dl-raised"
          >
            Duplicate
          </Link>
        )}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="rounded-lg p-1 text-dl-muted hover:bg-dl-sunken hover:text-dl-text"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="flex flex-col gap-6 px-6 py-5">
        {!task && details.isLoading && (
          <p className="text-dl-muted">Loading…</p>
        )}
        {details.isError && (
          <p role="alert" className="text-dl-danger">
            Could not load the task's details:{' '}
            {(details.error as Error)?.message}
          </p>
        )}
        {task && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={taskStatus(task)} />
              <IssuesIndicator issues={computationIssues(task)} />
              <span className="text-sm text-dl-muted">
                Revision {task.revision} · created{' '}
                {formatDateTime(task.createdAt)} by {task.createdBy}
              </span>
              <TraceLink traceId={task.traceId} logsUrl={task.logsUrl} />
            </div>
            {task.description && (
              <p className="text-dl-text">{task.description}</p>
            )}

            <ItemsSection
              taskId={taskId}
              summary={task.computation}
              canStop={canStopComputations}
            />

            {hasPredictions(task) && (
              <PredictionsSection
                taskId={taskId}
                canSettle={canSettlePredictions}
              />
            )}

            <section>
              <h4 className="mb-1 text-lg font-semibold text-dl-text">Data</h4>
              <dl>
                <Row label="Dataset">
                  <Table value={task.dataset} />
                </Row>
                <Row label="Earlier passes">
                  <Table value={task.bigQueryInitialPassesTableId} />
                </Row>
                <Row label="Predictions">
                  {details.data ||
                  task.predictionsConfiguration !== undefined ? (
                    <Table
                      value={
                        task.predictionsConfiguration?.bigQueryPredictionTableId
                      }
                    />
                  ) : (
                    <span className="text-dl-faint">…</span>
                  )}
                </Row>
                {task.predictionImportId != null && (
                  <Row label="Prediction import">
                    #{task.predictionImportId} · {task.predictionState ?? '—'}
                  </Row>
                )}
                {task.predictionsConfiguration && (
                  <Row label="Confidence shown">
                    {task.predictionsConfiguration.minConfidence ?? 0}–
                    {task.predictionsConfiguration.maxConfidence ?? 100}
                  </Row>
                )}
              </dl>
            </section>

            <section>
              <h4 className="mb-1 text-lg font-semibold text-dl-text">
                People
              </h4>
              <dl>
                <Row label="Labellers">
                  {task.taskers ? (
                    <People emails={labellersOf(task)} />
                  ) : (
                    <span className="text-dl-faint">…</span>
                  )}
                </Row>
                <Row label="Labellers per item">{task.requiredLabelers}</Row>
                <Row label="Reviewers">
                  <People emails={reviewersOf(task)} />
                </Row>
                {reviewersOf(task).length > 0 && (
                  <Row label="Cases per review">
                    {task.reviewConfiguration?.casesPerReview ??
                      task.casesPerReview}
                  </Row>
                )}
              </dl>
            </section>

            {details.data?.instructions && (
              <section>
                <h4 className="mb-1 text-lg font-semibold text-dl-text">
                  Instructions
                </h4>
                <p className="whitespace-pre-wrap text-dl-text">
                  {details.data.instructions}
                </p>
              </section>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
